import type { QueryClient } from '@tanstack/react-query';
import {
  getPlayControllerHeroV1QueryKey,
  getPlayControllerHeroesV1QueryKey,
  getPlayControllerPetsV1QueryKey,
  getPlayControllerBackgroundsV1QueryKey,
  getPlayControllerVoiceV1QueryKey,
  familyKeys,
} from '@kidshome/api-contract';

/**
 * Invalidate the hero/companion read surfaces after an effort completes (chore / habit / learning), so
 * the bond ring and the narrator voice react LIVE on the very screen where the effort happened — the core
 * motivational loop of the ADR-0095 hero overhaul. The backend already produces the bond gain and the
 * voice reaction on each effort event; the web simply re-reads them instead of waiting for a full reload.
 * Safe + cheap: no-ops when the kid has no hero.
 *
 * `childId` (the signed-in kid) scopes the ✨ BALANCE invalidation: unlocking/choosing a hero, pet, or
 * background SPENDS ✨ from the same ledger balance shown on the Wallet & Today star pills (read from
 * `familyKeys.balance` → `ChildBalanceResponse.stars`). The play read surfaces below carry their own copy
 * of `stars`, but the family balance cache is separate, so without this it stays stale after a ✨ spend
 * (mirrors usePurchaseCosmetic, which invalidates the balance on a cosmetic spend). Pass `null` to skip it.
 */
export function invalidateHeroSurfaces(qc: QueryClient, childId: string | null = null): void {
  void qc.invalidateQueries({ queryKey: getPlayControllerHeroV1QueryKey() });
  // The hero COLLECTION (ADR-0101) — owned/chosen flags + the ✨ balance move when a hero is unlocked/chosen.
  void qc.invalidateQueries({ queryKey: getPlayControllerHeroesV1QueryKey() });
  // The PET collection (ADR-0108) — owned/equipped flags + the ✨ balance move when a pet is unlocked/equipped.
  void qc.invalidateQueries({ queryKey: getPlayControllerPetsV1QueryKey() });
  // The BACKGROUND collection (ADR-0110 §7 · ADR-0111) — owned/equipped + ✨ balance move on unlock/equip.
  void qc.invalidateQueries({ queryKey: getPlayControllerBackgroundsV1QueryKey() });
  // The cosmetics CATALOG + EQUIPPED looks (ADR-0111) — both are hero-SCOPED, so they change when the kid
  // switches hero (a switch must show the NEW hero's looks, never the previous hero's). These use the
  // `familyKeys` family-hook keys (the same the purchase/equip features invalidate) — NOT the generated key,
  // which never matched, leaving the wardrobe showing the old hero's looks after a switch.
  void qc.invalidateQueries({ queryKey: familyKeys.cosmeticsCatalog() });
  void qc.invalidateQueries({ queryKey: familyKeys.cosmeticsEquipped() });
  void qc.invalidateQueries({ queryKey: getPlayControllerVoiceV1QueryKey() });
  // The Wallet & Today ✨ pills read the family balance cache (NOT the play surfaces above), so reconcile it
  // after a ✨ spend; scoped to the signed-in kid (self-scoped, like cosmetics). No-op when childId is null.
  if (childId) {
    void qc.invalidateQueries({ queryKey: familyKeys.balance(childId) });
  }
}
